import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Chip, Paper, RadioGroup, FormControlLabel, Radio,
  Select, MenuItem, Divider
} from '@mui/material';
import {
  Numbers as NumbersIcon,
  TextFields as TextIcon,
  ToggleOn as BooleanIcon,
  CalendarMonth as DateTimeIcon,
  Category as CategoryIcon,
  FormatListNumbered as IntegerIcon
} from '@mui/icons-material';

// Enhanced date format patterns for comprehensive detection
export const DATE_FORMATS = [
  // ISO 8601 variants
  { 
    pattern: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d{1,6})?Z?$/, 
    format: 'ISO 8601', 
    example: '2024-01-01T10:30:00',
    jsFormat: 'YYYY-MM-DDTHH:mm:ss'
  },
  { 
    pattern: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d{1,6})?[+-]\d{2}:\d{2}$/, 
    format: 'ISO 8601 with Timezone', 
    example: '2024-01-01T10:30:00+05:00',
    jsFormat: 'YYYY-MM-DDTHH:mm:ssZ'
  },
  
  // SQL DateTime variants (this should catch your example!)
  { 
    pattern: /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?(\.\d{1,6})?$/, 
    format: 'SQL DateTime', 
    example: '2016-01-06 00:00:00',
    jsFormat: 'YYYY-MM-DD HH:mm:ss'
  },
  { 
    pattern: /^\d{4}-\d{1,2}-\d{1,2} \d{1,2}:\d{2}(:\d{2})?$/, 
    format: 'SQL DateTime (loose)', 
    example: '2016-1-6 0:00:00',
    jsFormat: 'YYYY-M-D H:mm:ss'
  },
  
  // Date only variants
  { 
    pattern: /^\d{4}-\d{2}-\d{2}$/, 
    format: 'ISO Date', 
    example: '2024-01-01',
    jsFormat: 'YYYY-MM-DD'
  },
  { 
    pattern: /^\d{4}-\d{1,2}-\d{1,2}$/, 
    format: 'ISO Date (loose)', 
    example: '2024-1-1',
    jsFormat: 'YYYY-M-D'
  },
  
  // US formats
  { 
    pattern: /^\d{1,2}\/\d{1,2}\/\d{4}$/, 
    format: 'US Date', 
    example: '1/31/2024',
    jsFormat: 'M/D/YYYY'
  },
  { 
    pattern: /^\d{2}\/\d{2}\/\d{4}$/, 
    format: 'US Date (padded)', 
    example: '01/31/2024',
    jsFormat: 'MM/DD/YYYY'
  },
  { 
    // Seconds are optional - matches both `1/31/2024 14:30:00` and `1/31/2024 14:30`
    pattern: /^\d{1,2}\/\d{1,2}\/\d{4} \d{1,2}:\d{2}(:\d{2})?$/, 
    format: 'US DateTime', 
    example: '1/31/2024 14:30:00',
    jsFormat: 'M/D/YYYY H:mm:ss'
  },
  { 
    pattern: /^\d{1,2}\/\d{1,2}\/\d{4} \d{1,2}:\d{2}(:\d{2})? (AM|PM)$/i, 
    format: 'US DateTime 12h', 
    example: '1/31/2024 2:30:00 PM',
    jsFormat: 'M/D/YYYY h:mm:ss A'
  },
  
  // European formats
  { 
    // Seconds are optional - matches both `31/1/2024 14:30:00` and `31/1/2024 14:30`
    pattern: /^\d{1,2}\/\d{1,2}\/\d{4} \d{1,2}:\d{2}(:\d{2})?$/, 
    format: 'EU DateTime', 
    example: '31/1/2024 14:30:00',
    jsFormat: 'D/M/YYYY H:mm:ss'
  },
  { 
    pattern: /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}(:\d{2})?$/, 
    format: 'EU DateTime (padded)', 
    example: '31/01/2024 14:30:00',
    jsFormat: 'DD/MM/YYYY HH:mm:ss'
  },
  { 
    pattern: /^\d{1,2}-\d{1,2}-\d{4}$/, 
    format: 'EU Date', 
    example: '31-1-2024',
    jsFormat: 'D-M-YYYY'
  },
  { 
    pattern: /^\d{2}-\d{2}-\d{4}$/, 
    format: 'EU Date (padded)', 
    example: '31-01-2024',
    jsFormat: 'DD-MM-YYYY'
  },
  { 
    pattern: /^\d{1,2}\.\d{1,2}\.\d{4}$/, 
    format: 'German Date', 
    example: '31.1.2024',
    jsFormat: 'D.M.YYYY'
  },
  
  // Reverse date formats
  { 
    pattern: /^\d{4}\/\d{1,2}\/\d{1,2}$/, 
    format: 'Year First', 
    example: '2024/1/31',
    jsFormat: 'YYYY/M/D'
  },
  
  // Unix timestamps
  { 
    pattern: /^\d{10}$/, 
    format: 'Unix Timestamp', 
    example: '1704067800',
    jsFormat: 'unix'
  },
  { 
    pattern: /^\d{13}$/, 
    format: 'Unix Timestamp (ms)', 
    example: '1704067800000',
    jsFormat: 'unix_ms'
  },
  
  // Excel/Other formats
  { 
    pattern: /^\d{1,2}-[A-Za-z]{3}-\d{4}$/, 
    format: 'Excel Date', 
    example: '1-Jan-2024',
    jsFormat: 'D-MMM-YYYY'
  },
  { 
    pattern: /^[A-Za-z]{3} \d{1,2}, \d{4}$/, 
    format: 'US Long Date', 
    example: 'Jan 1, 2024',
    jsFormat: 'MMM D, YYYY'
  }
];

// Enhanced data type options with icons and detailed descriptions
export const DATA_TYPE_OPTIONS = [
  { 
    value: 'numeric', 
    label: 'Numeric (Continuous)', 
    icon: <NumbersIcon sx={{ fontSize: 20 }} />,
    color: 'success',
    description: 'Decimal numbers that can take any value within a range',
    examples: 'price, temperature, weight, percentage, revenue',
    characteristics: ['Can be measured with decimal precision', 'Supports arithmetic operations', 'Meaningful mean/median']
  },
  { 
    value: 'integer', 
    label: 'Integer (Discrete)', 
    icon: <IntegerIcon sx={{ fontSize: 20 }} />,
    color: 'success',
    description: 'Countable whole numbers with distinct values',
    examples: 'count, age, quantity, year, number of employees',
    characteristics: ['Whole numbers only', 'Can be counted in units', 'Finite values between points']
  },
  { 
    value: 'text', 
    label: 'Text/String', 
    icon: <TextIcon sx={{ fontSize: 20 }} />,
    color: 'primary',
    description: 'Text categories, labels, or unstructured text',
    examples: 'status, name, description, category, comments',
    characteristics: ['Text values', 'Categories or labels', 'Unstructured content']
  },
  { 
    value: 'boolean', 
    label: 'Boolean/Binary', 
    icon: <BooleanIcon sx={{ fontSize: 20 }} />,
    color: 'info',
    description: 'True/false, yes/no, or binary indicator values',
    examples: 'active, enabled, is_premium, completed, verified',
    characteristics: ['Only two possible values', 'Binary indicators', 'Yes/No questions']
  },
  { 
    value: 'datetime', 
    label: 'Date & Time', 
    icon: <DateTimeIcon sx={{ fontSize: 20 }} />,
    color: 'warning',
    description: 'Date and time values with timestamp information',
    examples: 'created_at, timestamp, order_date, last_login',
    characteristics: ['Date and time combined', 'Temporal ordering', 'Time-series data']
  },
  { 
    value: 'date', 
    label: 'Date Only', 
    icon: <DateTimeIcon sx={{ fontSize: 20 }} />,
    color: 'warning',
    description: 'Date values without time information',
    examples: 'birth_date, start_date, deadline, holiday',
    characteristics: ['Date without time', 'Day-level precision', 'Calendar events']
  },
  { 
    value: 'categorical', 
    label: 'Categorical', 
    icon: <CategoryIcon sx={{ fontSize: 20 }} />,
    color: 'secondary',
    description: 'Categories with limited set of possible values',
    examples: 'product_type, region, status, grade, tier',
    characteristics: ['Limited unique values', 'Grouping/classification', 'Nominal or ordinal']
  }
];

// Map FieldMappingBuilder types to ColumnMapper types
export const FIELD_MAPPING_TYPE_MAP = {
  'continuous_numerical': 'numeric',
  'discrete_numerical': 'integer',
  'categorical_text': 'text',
  'binary_boolean': 'boolean',
  'datetime_timestamp': 'datetime'
};

// Smart date format detection algorithm 
export const detectDateFormat = (values) => {
  if (!values || values.length === 0) return null;
  
  const nonEmptyValues = values.filter(v => v && v.toString().trim() !== '').slice(0, 500);
  if (nonEmptyValues.length === 0) return null;
  
  // Phase 1: Disambiguate slash formats (MM/DD vs DD/MM)
  const disambiguateSlashFormat = (vals) => {
    let mmddEvidence = 0;
    let ddmmEvidence = 0;
    
    vals.forEach(value => {
      const slashMatch = value.toString().trim().match(/^(\d{1,2})\/(\d{1,2})\/\d{4}/);
      if (slashMatch) {
        const first = parseInt(slashMatch[1]);
        const second = parseInt(slashMatch[2]);
        
        if (first > 12 && second <= 12) {
          ddmmEvidence++; // Must be DD/MM (day > 12)
        } else if (second > 12 && first <= 12) {
          mmddEvidence++; // Must be MM/DD (month > 12 impossible)
        }
      }
    });
    
    if (ddmmEvidence > 0) return 'DD/MM';
    if (mmddEvidence > 0) return 'MM/DD';
    
    // If all ambiguous, use additional heuristics
    const dayFirstProbable = vals.some(v => {
      const match = v.toString().match(/^(\d{1,2})\/(\d{1,2})\/\d{4}/);
      if (match) {
        const first = parseInt(match[1]);
        const second = parseInt(match[2]);
        return first > second && first <= 31 && second <= 12;
      }
      return false;
    });
    
    return dayFirstProbable ? 'DD/MM' : 'MM/DD';
  };
  
  // Phase 2: Test each format with enhanced logic
  const formatScores = [];
  
  for (const formatInfo of DATE_FORMATS) {
    let matchingValues = [];
    let score = 0;
    
    // Special handling for slash formats
    if ((formatInfo.format.includes('US Date') || formatInfo.format.includes('EU Date')) && formatInfo.pattern.source.includes('\\/')) {
      const slashDisambiguation = disambiguateSlashFormat(nonEmptyValues);
      
      if (formatInfo.format.includes('US') && slashDisambiguation === 'MM/DD') {
        matchingValues = nonEmptyValues.filter(v => formatInfo.pattern.test(v.toString().trim()));
        score = matchingValues.length / nonEmptyValues.length;
      } else if (formatInfo.format.includes('EU') && slashDisambiguation === 'DD/MM') {
        matchingValues = nonEmptyValues.filter(v => formatInfo.pattern.test(v.toString().trim()));
        score = matchingValues.length / nonEmptyValues.length;
      } else {
        continue; // Skip this format if disambiguation doesn't match
      }
    } else {
      // Standard pattern matching for non-ambiguous formats
      matchingValues = nonEmptyValues.filter(v => formatInfo.pattern.test(v.toString().trim()));
      score = matchingValues.length / nonEmptyValues.length;
    }
    
    if (score >= 0.8) {
      formatScores.push({ formatInfo, score, matchingValues });
    }
  }
  
  // Phase 3: Select best format
  if (formatScores.length > 0) {
    formatScores.sort((a, b) => b.score - a.score);
    const bestFormat = formatScores[0];
    return bestFormat.formatInfo;
  }
  
  // Phase 4: Fallback analysis
  const sampleValue = nonEmptyValues[0].toString().trim();
  
  if (/^\d{4}-\d{1,2}-\d{1,2} \d{1,2}:\d{1,2}(:\d{1,2})?/.test(sampleValue)) {
    return {
      pattern: /^\d{4}-\d{1,2}-\d{1,2} \d{1,2}:\d{1,2}(:\d{1,2})?/,
      format: 'SQL DateTime',
      example: sampleValue,
      jsFormat: 'YYYY-MM-DD HH:mm:ss'
    };
  }
  
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(sampleValue)) {
    return {
      pattern: /^\d{4}-\d{1,2}-\d{1,2}$/,
      format: 'ISO Date',
      example: sampleValue,
      jsFormat: 'YYYY-MM-DD'
    };
  }
  
  if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(sampleValue)) {
    // Don't blindly assume US - run the day/month disambiguator on the
    // sample so files like `4/20/2021 0:02` (no seconds) still resolve to
    // the correct EU vs US format and the backend parser gets the right
    // dayfirst hint.
    const slashDisambiguation = disambiguateSlashFormat(nonEmptyValues);
    const isEU = slashDisambiguation === 'DD/MM';
    return {
      pattern: /^\d{1,2}\/\d{1,2}\/\d{4} \d{1,2}:\d{1,2}(:\d{1,2})?/,
      format: isEU ? 'EU DateTime' : 'US DateTime',
      example: sampleValue,
      jsFormat: isEU ? 'D/M/YYYY H:mm:ss' : 'M/D/YYYY H:mm:ss'
    };
  }
  
  return null;
};

// Validate if values can be converted to valid dates
export const validateDateValues = (values, formatInfo = null) => {
  if (!values || values.length === 0) return { isValid: false, errors: ['No values to validate'] };
  
  const nonEmptyValues = values.filter(v => v && v.toString().trim() !== '');
  if (nonEmptyValues.length === 0) return { isValid: false, errors: ['No non-empty values'] };
  
  const errors = [];
  let validCount = 0;
  
  // Test all non-empty values
  nonEmptyValues.forEach((value, index) => {
    const strValue = value.toString().trim();
    let isValid = false;
    
    try {
      const parseSqlDateTime = (input) => {
        const match = input.match(/^(\d{4})-(\d{1,2})-(\d{1,2}) (\d{1,2}):(\d{2}):(\d{2})$/);
        if (!match) return null;
        const [, year, month, day, hour, minute, second] = match.map(Number);
        if (
          month < 1 || month > 12 ||
          day < 1 || day > 31 ||
          hour < 0 || hour > 23 ||
          minute < 0 || minute > 59 ||
          second < 0 || second > 59
        ) {
          return null;
        }
        const parsed = new Date(year, month - 1, day, hour, minute, second);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      };

      if (formatInfo?.jsFormat === 'unix') {
        const timestamp = parseInt(strValue);
        if (!isNaN(timestamp)) {
          const date = new Date(timestamp * 1000);
          isValid = !isNaN(date.getTime()) && date.getFullYear() > 1970 && date.getFullYear() < 2100;
        }
      } else if (formatInfo?.jsFormat === 'unix_ms') {
        const timestamp = parseInt(strValue);
        if (!isNaN(timestamp)) {
          const date = new Date(timestamp);
          isValid = !isNaN(date.getTime()) && date.getFullYear() > 1970 && date.getFullYear() < 2100;
        }
      } else if (formatInfo?.jsFormat?.includes('YYYY-M-D') || formatInfo?.format?.includes('SQL DateTime')) {
        const parsed = parseSqlDateTime(strValue);
        isValid = Boolean(parsed);
      } else {
        // Try to parse as date
        const date = new Date(strValue);
        isValid = !isNaN(date.getTime()) && 
                  date.getFullYear() > 1900 && 
                  date.getFullYear() < 2100 &&
                  strValue !== 'Invalid Date';
        
        // Additional validation: check if it looks like a date.
        // Seconds are optional everywhere - we accept both `H:mm` and
        // `H:mm:ss` so files like `4/20/2021 0:02` validate cleanly.
        const datePatterns = [
          /^\d{4}-\d{2}-\d{2}/, // YYYY-MM-DD
          /^\d{4}-\d{1,2}-\d{1,2} \d{1,2}:\d{2}(:\d{2})?/, // YYYY-M-D H:mm[:ss]
          /^\d{1,2}\/\d{1,2}\/\d{4}/, // M/D/YYYY
          /^\d{2}-\d{2}-\d{4}/, // DD-MM-YYYY
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?/ // ISO datetime
        ];
        
        const looksLikeDate = datePatterns.some(pattern => pattern.test(strValue));
        isValid = isValid && looksLikeDate;
      }
      
      if (isValid) {
        validCount++;
      } else {
        if (errors.length < 5) { // Limit error messages
          errors.push(`"${strValue}" is not a valid date`);
        }
      }
    } catch (e) {
      if (errors.length < 5) {
        errors.push(`"${strValue}" cannot be parsed as date`);
      }
    }
  });
  
  const validPercentage = validCount / nonEmptyValues.length;
  const isValid = validPercentage >= 0.9; // Require 90% valid dates
  
  return {
    isValid,
    validCount,
    totalValues: nonEmptyValues.length,
    validPercentage: Math.round(validPercentage * 100),
    errors: errors.slice(0, 3), // Show only first 3 errors
    totalErrors: errors.length
  };
};

// Type Configuration Dialog Component
export const TypeConfigDialog = ({ open, onClose, columnName, currentType, onTypeChange, dataTypeOptions = DATA_TYPE_OPTIONS }) => {
  const [selectedType, setSelectedType] = useState(currentType);

  useEffect(() => {
    setSelectedType(currentType);
  }, [currentType, open]);

  const handleSave = () => {
    onTypeChange(selectedType);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Configure Data Type for "{columnName}"
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select the most appropriate data type for this column:
        </Typography>
        
        <RadioGroup value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
          {dataTypeOptions.map((option) => (
            <Paper 
              key={option.value}
              variant="outlined"
              sx={{ 
                mb: 1,
                border: selectedType === option.value ? 2 : 1,
                borderColor: selectedType === option.value ? `${option.color}.main` : 'divider'
              }}
            >
              <FormControlLabel
                value={option.value}
                control={<Radio size="small" color={option.color} sx={{ ml: 1 }} />}
                label={
                  <Box sx={{ py: 1, pr: 2, width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      {option.icon}
                      <Typography variant="body2" sx={{ ml: 1, fontWeight: 600 }}>
                        {option.label}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.85rem' }}>
                      {option.description}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {option.examples.split(', ').slice(0, 6).map((example, idx) => (
                        <Chip
                          key={idx}
                          label={example.trim()}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.65rem', 
                            height: 18,
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      ))}
                      {option.examples.split(', ').length > 6 && (
                        <Chip
                          label="..."
                          size="small"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.65rem', 
                            height: 18,
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                }
                sx={{ 
                  width: '100%', 
                  m: 0,
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
              />
            </Paper>
          ))}
        </RadioGroup>
      </DialogContent>
      <DialogActions sx={{ pt: 1 }}>
        <Button onClick={onClose} size="small">Cancel</Button>
        <Button onClick={handleSave} variant="contained" size="small">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Date Format Select Component
export const DateFormatSelect = ({ 
  value, 
  onChange, 
  disabled = false, 
  size = 'small',
  includeCustom = false,
  customIcon = null,
  sx = {}
}) => {
  const mergedSx = {
    width: '100%',
    '& .MuiSelect-select': {
      backgroundColor: 'success.50',
      color: 'success.800',
      borderColor: 'success.main',
      fontSize: '0.8rem',
      py: 0.5,
      px: 1,
      display: 'flex',
      alignItems: 'center',
      gap: 0.5
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'success.main',
      borderWidth: 2
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'success.dark',
      borderWidth: 2
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'success.dark',
      borderWidth: 2
    },
    ...sx
  };

  return (
    <Select
      size={size}
      value={value || 'none'}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      disabled={disabled}
      displayEmpty
      sx={mergedSx}
      renderValue={(value) => {
        if (value === 'none') {
          return (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              Select format...
            </Typography>
          );
        }
        const format = DATE_FORMATS.find(f => f.format === value);
        if (format) {
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, lineHeight: 1.2 }}>
                {format.format}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'success.700', lineHeight: 1 }}>
                {format.example}
              </Typography>
            </Box>
          );
        } else if (value === 'Custom Format' || value.startsWith('Custom')) {
          // Handle custom formats
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, lineHeight: 1.2 }}>
                Custom Format
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'success.700', lineHeight: 1 }}>
                User defined
              </Typography>
            </Box>
          );
        }
        return value;
      }}
    >
      <MenuItem value="none">
        <Box>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            No format detected
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            Click to select format
          </Typography>
        </Box>
      </MenuItem>
      <Divider />
      {DATE_FORMATS.map((format) => (
        <MenuItem key={format.format} value={format.format}>
          <Box>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{format.format}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {format.example}
            </Typography>
          </Box>
        </MenuItem>
      ))}
      {includeCustom && (
        <>
          <Divider />
          <MenuItem 
            value="custom"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange('custom');
              // Close the select menu
              const selectElement = e.target.closest('.MuiSelect-root');
              if (selectElement) {
                selectElement.click();
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {customIcon}
              <Typography variant="body2" sx={{ fontSize: '0.8rem', ml: customIcon ? 1 : 0 }}>
                Custom Format...
              </Typography>
            </Box>
          </MenuItem>
        </>
      )}
    </Select>
  );
};
